import { Button, Checkbox, Form, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import EmailIcon from "../../../../assets/icons/EmailIcon";
import CardTitle from "../../../../components/common/CardTitle";
import CustomCard from "../../../../components/common/CustomCard";
import InfoItem from "../../../../components/common/InfoItem";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import Loader from "../../../../components/ui/Loader";
import {
  useMakeSuperAdminMutation,
  useRoleStatusQuery,
} from "../../../../features/emp-setting/employee/employeeApi";
import {
  useAssignUserToProjectRoleMutation,
  useDeleteRoleMutation,
  useGetProjectsByUserQuery,
} from "../../../../features/portal/Products/productsApi";
import { useGetSingleUserQuery } from "../../../../features/portal/user/userApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { removeCountryCodeFromNumber } from "../../../../utils/utils";
import EmployeeFileUpload from "../../../admin/Employee/_components/EmployeeFileUpload";
import UserProductManage from "./_components/UserProductManage";

const backLink = "/admin/user-access-permission/user-list";

type TList = {
  productId?: number;
  productName?: string;
  groupId?: number;
  groupName?: string;
  isEdit?: boolean;
};

export default function UserEditPermission() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const { user, role } = useAppSelector((state) => state.auth);
  const [list, setList] = useState<TList[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const [
    assignUserToProjectRole,
    {
      isLoading: isAssignLoading,
      isError: isAssignError,
      error: assignError,
    },
  ] = useAssignUserToProjectRoleMutation();
  const [
    deleteRole,
    { isLoading: isDeleteLoading, isError: isDeleteError, error: deleteError },
  ] = useDeleteRoleMutation();
  useError(isAssignError, assignError, "Group permission assign fail");
  useError(isDeleteError, deleteError, "Group permission remove fail");
  const { handleOpen, handleClose, open } = useModal<
    boolean | any,
    "CONFIRM_SUPER" | "CONFIRM_CREATE"
  >();
  const [
    makeSuperAdmin,
    {
      isLoading: isSuperAdminLoading,
      isError: isSuperAdminError,
      error: superAdminError,
      isSuccess: isSuperAdminSuccess,
    },
  ] = useMakeSuperAdminMutation();
  useError(isSuperAdminError, superAdminError, "Make super admin fail");

  const {
    isError: isRoleError,
    error: roleError,
    isLoading: isRoleLoading,
    data: roleData,
  } = useRoleStatusQuery(String(id), {
    skip: !id,
  });
  useError(isRoleError, roleError, "Get role fail");

  const isLoggedInSuperAdmin =
    role === "SUPER_ADMIN" ||
    user?.data?.permission === "SUPER_ADMIN" ||
    Boolean(roleData?.data?.isSuperAdmin);

  const {
    data: singleUserData,
    isLoading: isSingleUserLoading,
    isError: isSingleUserError,
    error: singleUserError,
  } = useGetSingleUserQuery(String(id), {
    skip: !id,
  });
  useError(isSingleUserError, singleUserError, "User fetch fail");
  const {
    data: userProjectData,
    isLoading: isUserProjectLoading,
    isError: isUserProjectError,
    error: userProjectError,
  } = useGetProjectsByUserQuery(
    {
      params: { userid: id },
    },
    {
      skip: !id,
    }
  );
  useError(isUserProjectError, userProjectError, "Get group data fail");
  const singleUser = singleUserData?.data;
  const groupPermissions = userProjectData?.data || [];

  useEffect(() => {
    setIsInitialized(false);
  }, [id]);

  useEffect(() => {
    if (!isUserProjectLoading && !isInitialized) {
      if (groupPermissions?.length) {
        const permissionList = groupPermissions.map((el: any) => ({
          productId: el?.project?.id,
          productName: el?.project?.name,
          groupId: el?.role?.id,
          groupName: el?.role?.roleName,
          isEdit: false,
        }));

        setList(permissionList);
      }
      setIsInitialized(true);
    }
  }, [groupPermissions, isUserProjectLoading, isInitialized]);

  const onFinish = () => {
    let currentList = [...list];
    const pendingProductId = addForm.getFieldValue("productId");
    const pendingGroupId = addForm.getFieldValue("groupId");

    if (pendingProductId && pendingGroupId) {
      const alreadyInList = currentList.some(
        (el) => Number(el?.productId) === Number(pendingProductId)
      );
      if (!alreadyInList) {
        currentList.push({
          productId: Number(pendingProductId),
          groupId: Number(pendingGroupId),
        });
        setList(currentList);
        addForm.resetFields(["productId", "groupId"]);
      }
    } else if (pendingProductId && !pendingGroupId) {
      toast("error", "Please select a user group for the selected product.");
      return;
    } else if (!pendingProductId && pendingGroupId) {
      toast("error", "Please select a product.");
      return;
    }

    if (!currentList.length) {
      toast("error", "You must choose at least one role group.");
      return;
    }

    const newValues = {
      groupPermissionIds: currentList?.map((el) => el?.groupId),
      userId: Number(id),
    };

    handleOpen({
      type: "CONFIRM_CREATE",
      data: { newValues, finalList: currentList },
    });
  };

  useEffect(() => {
    if (!!Object.keys(singleUser || {}).length) {
      form.setFieldsValue({
        username: singleUser?.username,
        email: singleUser?.email,
        mobile_number: singleUser?.mobile
          ? removeCountryCodeFromNumber(singleUser?.mobile)
          : undefined,
        logo: singleUser?.photo,
      });
    }
  }, [form, singleUser]);

  useEffect(() => {
    if (isSuperAdminSuccess) {
      toast(
        "success",
        open?.data
          ? "Successfully make this user a super admin"
          : "Successfully remove super admin status from this user"
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdminSuccess]);

  const handleUpdatePermissions = async (targetList: TList[] = list) => {
    if (!id) return;

    const userId = Number(id);
    const existingAssignments = groupPermissions || [];

    // Map existing assignments: projectId -> roleId
    const existingMap = new Map<number, number>();
    existingAssignments.forEach((el: any) => {
      if (el?.project?.id && el?.role?.id) {
        existingMap.set(Number(el.project.id), Number(el.role.id));
      }
    });

    // Map target assignments: projectId -> roleId
    const currentMap = new Map<number, number>();
    targetList.forEach((el) => {
      if (el?.productId && el?.groupId) {
        currentMap.set(Number(el.productId), Number(el.groupId));
      }
    });

    // 1. Roles to remove: in existing but not in target, OR role changed
    const toRemove: { projectId: number; roleId: number }[] = [];
    existingMap.forEach((roleId, projectId) => {
      const newRoleId = currentMap.get(projectId);
      if (newRoleId === undefined || newRoleId !== roleId) {
        toRemove.push({ projectId, roleId });
      }
    });

    // 2. Roles to assign: in target but not in existing, OR role changed
    const toAssign: { projectId: number; roleId: number }[] = [];
    currentMap.forEach((roleId, projectId) => {
      const oldRoleId = existingMap.get(projectId);
      if (oldRoleId === undefined || oldRoleId !== roleId) {
        toAssign.push({ projectId, roleId });
      }
    });

    try {
      if (toRemove.length > 0) {
        await Promise.all(
          toRemove.map((item) =>
            deleteRole({
              userId,
              projectId: item.projectId,
              roleId: item.roleId,
            }).unwrap()
          )
        );
      }

      if (toAssign.length > 0) {
        await Promise.all(
          toAssign.map((item) =>
            assignUserToProjectRole({
              userId,
              projectId: item.projectId,
              roleId: item.roleId,
            }).unwrap()
          )
        );
      }

      toast("success", "Group permission updated successfully");
      handleClose();
      navigate(backLink);
    } catch (err: any) {
      toast(
        "error",
        err?.data?.message || err?.message || "Failed to update permissions"
      );
    }
  };

  return (
    <SidebarLayout title="Update Permission">
      {open?.type === "CONFIRM_SUPER" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to ${
            open?.data
              ? "make this user a super admin"
              : "remove super admin status from this user"
          }?`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isSuperAdminLoading}
          handleOk={() => {
            makeSuperAdmin({
              body: { enable: open?.data },
              id: Number(id),
            });
          }}
        />
      )}

      {open?.type === "CONFIRM_CREATE" && (
        <ConfirmModal
          confirmTitle="Confirm Action"
          questionText={`Are you sure you want to update permission in this user?`}
          okText="Yes, Confirmed"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isAssignLoading || isDeleteLoading}
          handleOk={() => {
            void handleUpdatePermissions(open?.data?.finalList || list);
          }}
        />
      )}

      {isSingleUserLoading || isUserProjectLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <CustomCard className=" p-6 pb-0 rounded-b-none">
            <CardTitle>User Info</CardTitle>

            <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-6 pt-4 mb-6">
              {/* <Form.Item
              label="Select User"
              name="userId"
              rules={[{ required: true, message: "Please select user" }]}
            >
              <Input placeholder="Enter User Name" disabled />
            </Form.Item> */}
              <Form.Item label="User Name" name="username">
                <Input placeholder="Enter User Name" disabled />
              </Form.Item>

              <Form.Item label="Email ID" name="email">
                <Input
                  placeholder="Enter email id"
                  prefix={<EmailIcon />}
                  disabled
                />
              </Form.Item>

              <Form.Item label="Contact No" name="mobile_number">
                <Input prefix={"+88"} size="large" placeholder="01" disabled />
              </Form.Item>

              {/* <Form.Item label="Address" name="address">
              <Input placeholder="Enter  address" disabled />
            </Form.Item> */}

              <Form.Item
                name="logo"
                label="User Photo"
                className="mb-4"
                valuePropName="fileUrl"
              >
                <EmployeeFileUpload
                  form={form}
                  accept=".png, .jpg, .jpeg"
                  name="logo"
                  fileSize={5}
                  onChange={(url) =>
                    form.setFieldsValue({
                      logo: url,
                    })
                  }
                  disabled
                />
              </Form.Item>
            </div>

            <CardTitle>User Role Group</CardTitle>
            {isLoggedInSuperAdmin && (
              <InfoItem rootClassName="!py-3" className="space-x-1">
                <Checkbox
                  id="supper_admin"
                  checked={roleData?.data?.selectedUserIsSuperAdmin ?? false}
                  disabled={isRoleLoading || !id}
                  onChange={(checked) => {
                    handleOpen({
                      type: "CONFIRM_SUPER",
                      data: checked.target.checked,
                    });
                  }}
                />{" "}
                <label
                  htmlFor="supper_admin"
                  className={`text-accent select-none ${
                    isRoleLoading || !id ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  Assign as Super Admin
                </label>
              </InfoItem>
            )}

            <UserProductManage list={list} setList={setList} addForm={addForm} />

            <Space className="w-full my-4" size={12}>
              <Link to={backLink}>
                <Button
                  className="normal-dark-button modal-button"
                  size="middle"
                >
                  Cancel
                </Button>
              </Link>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                // loading={id ? updateSingleUserIsLoading : isCreateUserLoading}
              >
                {id ? "Update" : "Create user"}
              </Button>
            </Space>
          </CustomCard>
        </Form>
      )}
    </SidebarLayout>
  );
}
